import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import OrdersService from '../services/OrdersService';
import { Input, Label, FormGroup } from 'reactstrap';
import BillingAddressForm from '../components/BillingAddressForm';
import DeliveryAddressForm from '../components/DeliveryAddressForm';
import OrderedTable from '../components/OrderedTable';
import { validateCheckoutForm } from '../utils/checkoutValidation';

const stateLookup = {
    'Alabama': 'AL',
    'Alaska': 'AK',
    'Arizona': 'AZ',
    'Arkansas': 'AR',
    'California': 'CA',
    'Colorado': 'CO',
    'Connecticut': 'CT',
    'Delaware': 'DE',
    'District of Columbia': 'DC',
    'Florida': 'FL',
    'Georgia': 'GA',
    'Guam': 'GU',
    'Hawaii': 'HI',
    'Idaho': 'ID',
    'Illinois': 'IL',
    'Indiana': 'IN',
    'Iowa': 'IA',
    'Kansas': 'KS',
    'Kentucky': 'KY',
    'Louisiana': 'LA',
    'Maine': 'ME',
    'Maryland': 'MD',
    'Massachusetts': 'MA',
    'Michigan': 'MI',
    'Minnesota': 'MN',
    'Mississippi': 'MS',
    'Missouri': 'MO',
    'Montana': 'MT',
    'Nebraska': 'NE',
    'Nevada': 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    'Ohio': 'OH',
    'Oklahoma': 'OK',
    'Oregon': 'OR',
    'Pennsylvania': 'PA',
    'Puerto Rico': 'PR',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    'Tennessee': 'TN',
    'Texas': 'TX',
    'Utah': 'UT',
    'Vermont': 'VT',
    'Virginia': 'VA',
    'Washington': 'WA',
    'West Virginia': 'WV',
    'Wisconsin': 'WI',
    'Wyoming': 'WY'
};

export default function Order(props) {
    const [purchaseId, setPurchaseId] = useState(11);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const { orderId } = useParams()
    const [order, setOrder] = useState();
    const [sameAddress, setSameAddress] = useState(true);
    const [shippingInfo, setShippingInfo] = useState(null); // Track shipping status for validation
    const orderRef = useRef();
    const [deliveryAddress, setDeliveryAddress] = useState({
        firstName: '',
        lastName: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: ''
    });

    // Detect PO Box pattern
    const checkPOBox = (value) => {
        if (!value) return false;
        const normalized = value
            .toLowerCase()
            .replace(/\./g, " ")  // Replace periods with spaces first
            .replace(/[^a-z0-9\s]/g, "")  // Remove other special chars
            .replace(/\s+/g, " ")  // Normalize multiple spaces to single space
            .trim();
        
        // PO Box detection patterns:
        // 1. Explicit "box" patterns: "P.O. Box", "PO Box", "Post Office Box", etc.
        // 2. Standalone "PO" or "P O" followed by numbers: "PO 123", "P.O. 456"
        // 3. Just "Box" followed by numbers: "Box 123"
        const isPoBox = /\b(p\s*o\s*box|post\s*office\s*box|pobox)\s*\d*\b/.test(normalized) ||
                        /\bp\s+o\s+\d+\b/.test(normalized) ||  // "P O 123" or "P.O. 123"
                        /\bpo\s+\d+\b/.test(normalized) ||     // "PO 123"
                        /\bbox\s+\d+\b/.test(normalized);      // "Box 123"
        
        // Debug logging
        if (isPoBox) {
            console.log('PO Box detected:', { original: value, normalized: normalized });
        }
        
        return isPoBox;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateCheckoutForm(event.currentTarget)) {
            event.stopPropagation();
            window.scrollTo(0, 0);
            return;
        }
        
        // Prevent double submission
        if (submittingRef.current || isSubmitting) {
            return;
        }
        
        // NEW: Wait for shipping info to load (prevents race condition)
        if (shippingInfo === null) {
            alert('Please wait a moment for the order information to load, then try again. If the problem persists, please refresh the page or contact your coach for assistance.');
            return;
        }
        
        // Existing validation: Block if address required (Type 1 = error state)
        if (shippingInfo.shippingType === 1) {
            // Server detected an error (incomplete address or PO Box)
            // Mark the appropriate address field as invalid
            const fieldId = sameAddress ? 'address' : 'deliveryaddress';
            const inputElement = document.getElementById(fieldId);
            if (inputElement) {
                inputElement.setCustomValidity(shippingInfo.message || 'Please provide a valid shipping address');
                inputElement.classList.add('is-invalid');
            }
            
            // Trigger validation display
            const form = document.querySelector('.needs-validation');
            if (form) {
                form.classList.add('was-validated');
            }
            
            window.scrollTo(0, 0);
            return;
        }
        
        // Enhanced validation for calculated shipping (Type 2)
        if (shippingInfo.shippingType === 2) {
            // Existing PO Box check
            let addressToCheck, address2ToCheck;
            
            if (sameAddress) {
                // Check billing address
                addressToCheck = event.target.address?.value || '';
                address2ToCheck = event.target.address2?.value || '';
            } else {
                // Check delivery address
                addressToCheck = event.target.deliveryaddress?.value || '';
                address2ToCheck = event.target.deliveryaddress2?.value || '';
            }
            
            if (checkPOBox(addressToCheck) || checkPOBox(address2ToCheck)) {
                // Mark the appropriate field as invalid
                const fieldId = sameAddress ? 'address' : 'deliveryaddress';
                const inputElement = document.getElementById(fieldId);
                if (inputElement) {
                    inputElement.setCustomValidity('PO Boxes cannot be used for shipping');
                    inputElement.classList.add('is-invalid');
                }
                
                // Trigger validation display
                const form = document.querySelector('.needs-validation');
                if (form) {
                    form.classList.add('was-validated');
                }
                
                window.scrollTo(0, 0);
                return;
            }
            
            // NEW: Pre-submit verification - make final API call with current form values
            setIsSubmitting(true);
            
            try {
                const customerName = `${event.target.firstName.value} ${event.target.lastName.value}`.trim();
                const finalShippingRequest = {
                    orderId: order.orderId,
                    customerName: customerName,
                    shippingAddress: sameAddress ? event.target.address.value : event.target.deliveryaddress.value,
                    shippingAddress2: sameAddress ? (event.target.address2?.value || '') : (event.target.deliveryaddress2?.value || ''),
                    shippingCity: sameAddress ? event.target.city.value : event.target.deliverycity.value,
                    shippingState: sameAddress ? event.target.state.value : event.target.deliverystate.value,
                    shippingZip: sameAddress ? event.target.zip.value : event.target.deliveryzip.value
                };
                
                const response = await axios.post('api/Orders/GetShippingCost', finalShippingRequest);
                const finalShippingData = response.data;
                
                // Check if address validation failed on final check
                if (finalShippingData.shippingType === 1) {
                    alert(finalShippingData.message || 'There is an issue with your shipping address. Please verify your address is complete and correct, then try again. If the problem persists, please contact your coach for assistance.');
                    setIsSubmitting(false);
                    return;
                }
                
                // Verify shipping cost matches what user sees
                if (finalShippingData.shippingType === 2) {
                    const displayedShipping = shippingInfo.totalShippingCost || 0;
                    const finalShipping = finalShippingData.totalShippingCost || 0;
                    
                    // Allow 1 cent tolerance for rounding
                    if (Math.abs(displayedShipping - finalShipping) > 0.01) {
                        alert(`The shipping cost has changed from $${displayedShipping.toFixed(2)} to $${finalShipping.toFixed(2)}. Please review the updated total below and resubmit your order. If you believe this is incorrect, please contact your coach for assistance.`);
                        
                        // Update displayed values
                        setShippingInfo({
                            shippingType: finalShippingData.shippingType,
                            totalShippingCost: finalShipping,
                            message: finalShippingData.message || ''
                        });
                        
                        setIsSubmitting(false);
                        return;
                    }
                    
                    // Final verification passed - set clientCalculatedShippingCost with confirmed value
                    order.clientCalculatedShippingCost = finalShipping;
                }
                
            } catch (error) {
                console.error('Error verifying final shipping cost:', error);
                alert('Unable to verify shipping cost. Please check your internet connection and try again. If the problem persists, please refresh the page or contact your coach for assistance.');
                setIsSubmitting(false);
                return;
            }
        }
        
        submittingRef.current = true;
        setIsSubmitting(true);
        getCreditCardToken(event);
        saveOrderDetails(event);
        saveBillingDetails(event);
        orderRef.current = order;
    };

    const enforceValidation = () => {
        const forms = document.querySelectorAll('.needs-validation')  // Loop over them and prevent submission
        Array.from(forms).forEach(form => {
            form.addEventListener('submit', event => {
                if (!form.checkValidity()) {
                    event.preventDefault()
                    event.stopPropagation()
                    //$(".is-invalid:first").focus();
                    window.scrollTo(0, 0);
                }
                form.classList.add('was-validated')
            }, false)
        });
    };

    const saveOrderDetails = (event) => {
        if (sameAddress) {
            order.supporterFirstName = event.target.firstName.value;
            order.supporterLastName = event.target.lastName.value;
            order.shippingAddress = event.target.address.value;
            order.shippingAddress2 = event.target.address2.value;
            order.shippingCity = event.target.city.value;
            order.shippingState = event.target.state.value;
            order.shippingZip = event.target.zip.value;
        } else {
            order.supporterFirstName = event.target.deliveryfirstName.value;
            order.supporterLastName = event.target.deliverylastName.value;
            order.shippingAddress = event.target.deliveryaddress.value;
            order.shippingAddress2 = event.target.deliveryaddress2.value;
            order.shippingCity = event.target.deliverycity.value;
            order.shippingState = event.target.deliverystate.value;
            order.shippingZip = event.target.deliveryzip.value;
        }
        order.supporterPhoneNumber = event.target.phone.value;
        order.donorEmail = event.target.email.value;

    }

    const saveBillingDetails = (event) => {
        order.payment.amount = order.orderTotal;
        order.payment.firstName = event.target.firstName.value;
        order.payment.lastName = event.target.lastName.value;
        order.payment.address1 = event.target.address.value;
        order.payment.address2 = event.target.address2.value;
        order.payment.city = event.target.city.value;
        order.payment.state = event.target.state.value;
        order.payment.zip = event.target.zip.value;
    }


    const getCreditCardToken = (event) => {
        var obj = {
            "holderName": event.target.holderName.value,
            "cardNumber": event.target.cardNumber.value,
            "cvv": event.target.cvv.value,
            "expiryMonth": event.target.expiryMonth.value,
            "expiryYear": event.target.expiryYear.value,
            "billingAddress": {
                "addressLine1": event.target.address.value,
                "addressLine2": event.target.address2.value,
                "city": event.target.city.value,
                "state": stateLookup[event.target.state.value],
                "zip": event.target.zip.value
            }
        }

        var raw = JSON.stringify(obj);

        var requestOptions = {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json'
            }),
            body: raw,
            redirect: 'follow'
        };

        fetch(order.gatewayUrl, requestOptions)
            .then(function (response) {
                // clear the CC errors out
                document.getElementById('ccErrors').innerHTML = ''

                // Store response details for potential error logging
                const responseStatus = response.status;
                const responseStatusText = response.statusText;
                const responseHeaders = {};
                response.headers.forEach((value, key) => {
                    responseHeaders[key] = value;
                });

                return response.json().then(result => ({
                    result: result,
                    status: responseStatus,
                    statusText: responseStatusText,
                    headers: responseHeaders
                }));
            })
            .then(function (responseData) {
                const result = responseData.result;
                
                if (result.hasOwnProperty('token')) {
                    console.log(result);
                    finishSubmit(result);
                }
                else {
                    submittingRef.current = false;
                    setIsSubmitting(false);
                    console.log(result);

                    if (result.hasOwnProperty('errors')) {
                        // Priority gave us specific validation errors - show them to user, no logging needed
                        result.errors.forEach(function (item) {
                            var liElement = document.createElement('li');
                            liElement.textContent = item.field + ": " + item.message;
                            document.getElementById('ccErrors').appendChild(liElement);
                        });
                    } else if (result.hasOwnProperty('errorMessage')) {
                        // Priority gave us a specific error message - show it to user, no logging needed
                        var liElement = document.createElement('li');
                        liElement.textContent = result.errorMessage;
                        document.getElementById('ccErrors').appendChild(liElement);
                    } else {
                        // Unknown error from Priority - log detailed info for troubleshooting
                        var liElement = document.createElement('li');
                        liElement.textContent = "An unknown payment error occurred";
                        document.getElementById('ccErrors').appendChild(liElement);
                        
                        // Log detailed tokenization failure for Priority troubleshooting (no PII)
                        const errorDetails = {
                            timestamp: new Date().toISOString(),
                            cardholderNameLength: obj.holderName?.length || 0,
                            cardholderInitials: obj.holderName ? obj.holderName.split(' ').map(n => n.charAt(0)).join('') : '',
                            gatewayUrl: order.gatewayUrl,
                            orderId: order.orderId,
                            fundraiserId: order.fundraiserId,
                            responseStatus: responseData.status,
                            responseStatusText: responseData.statusText,
                            responseHeaders: responseData.headers,
                            responseBody: JSON.stringify(result),
                            billingStateCode: obj.billingAddress?.state,
                            billingZipPrefix: obj.billingAddress?.zip?.substring(0, 3),
                            browserInfo: {
                                platform: navigator.platform,
                                language: navigator.language,
                                cookieEnabled: navigator.cookieEnabled
                            }
                        };
                        
                        const detailedMessage = `Card Tokenization Failed - Priority Gateway Response (Unknown Error):\n` +
                            `Timestamp: ${errorDetails.timestamp}\n` +
                            `Cardholder Initials: ${errorDetails.cardholderInitials} (${errorDetails.cardholderNameLength} chars)\n` +
                            `Order ID: ${errorDetails.orderId}\n` +
                            `Fundraiser ID: ${errorDetails.fundraiserId}\n` +
                            `Gateway URL: ${errorDetails.gatewayUrl}\n` +
                            `HTTP Status: ${errorDetails.responseStatus} ${errorDetails.responseStatusText}\n` +
                            `Response Body: ${errorDetails.responseBody}\n` +
                            `Billing State: ${errorDetails.billingStateCode}\n` +
                            `ZIP Prefix: ${errorDetails.billingZipPrefix}xxx\n` +
                            `Browser: ${errorDetails.browserInfo.platform} ${errorDetails.browserInfo.language}`;
                        
                        logMessage(detailedMessage, 4);
                    }
                }
            })
            .catch(function (error) {
                submittingRef.current = false;
                setIsSubmitting(false);

                // Log detailed network/fetch error for Priority troubleshooting (no PII)
                const errorMessage = error.message || error.toString();
                
                // Categorize the network error type for Priority
                let errorCategory = 'Unknown Network Error';
                if (errorMessage.includes('Load failed') || errorMessage.includes('Load Failed')) {
                    errorCategory = 'Load Failed - Possible DNS/Connection Issue';
                } else if (errorMessage.includes('Failed to fetch')) {
                    errorCategory = 'Failed to Fetch - Possible CORS/Firewall Issue';
                } else if (errorMessage.includes('NetworkError when attempting to fetch')) {
                    errorCategory = 'Network Error - Possible SSL/Certificate Issue';
                } else if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
                    errorCategory = 'Timeout - Gateway Response Too Slow';
                } else if (errorMessage.includes('abort') || errorMessage.includes('Abort')) {
                    errorCategory = 'Request Aborted - User/Browser Cancelled';
                }
                
                const errorDetails = {
                    timestamp: new Date().toISOString(),
                    cardholderNameLength: obj.holderName?.length || 0,
                    cardholderInitials: obj.holderName ? obj.holderName.split(' ').map(n => n.charAt(0)).join('') : '',
                    gatewayUrl: order.gatewayUrl,
                    orderId: order.orderId,
                    fundraiserId: order.fundraiserId,
                    errorType: error.name || 'Unknown',
                    errorMessage: errorMessage,
                    errorCategory: errorCategory,
                    billingStateCode: obj.billingAddress?.state,
                    billingZipPrefix: obj.billingAddress?.zip?.substring(0, 3),
                    browserInfo: {
                        platform: navigator.platform,
                        language: navigator.language,
                        cookieEnabled: navigator.cookieEnabled,
                        onLine: navigator.onLine,
                        connection: navigator.connection ? {
                            effectiveType: navigator.connection.effectiveType,
                            downlink: navigator.connection.downlink,
                            rtt: navigator.connection.rtt
                        } : 'Not Available'
                    }
                };
                
                const detailedMessage = `Card Tokenization Network Error - Priority Gateway:\n` +
                    `Timestamp: ${errorDetails.timestamp}\n` +
                    `Error Category: ${errorDetails.errorCategory}\n` +
                    `Cardholder Initials: ${errorDetails.cardholderInitials} (${errorDetails.cardholderNameLength} chars)\n` +
                    `Order ID: ${errorDetails.orderId}\n` +
                    `Fundraiser ID: ${errorDetails.fundraiserId}\n` +
                    `Gateway URL: ${errorDetails.gatewayUrl}\n` +
                    `Error Type: ${errorDetails.errorType}\n` +
                    `Error Message: ${errorDetails.errorMessage}\n` +
                    `Billing State: ${errorDetails.billingStateCode}\n` +
                    `ZIP Prefix: ${errorDetails.billingZipPrefix}xxx\n` +
                    `Browser: ${errorDetails.browserInfo.platform} ${errorDetails.browserInfo.language}\n` +
                    `Online Status: ${errorDetails.browserInfo.onLine}\n` +
                    `Connection: ${JSON.stringify(errorDetails.browserInfo.connection)}`;
                
                logMessage(detailedMessage, 4);
                alert("An unknown payment error occurred")
                return console.log('error', error);
            });
    };

    const finishSubmit = (response) => {
        orderRef.current.payment.token = response.token;
        console.log(orderRef.current);

        OrdersService.ProcessOrder(orderRef.current).then(res => {
            console.log(res.data);
            if (res.data == 'success') {
                alert("Order Successful!");

                axios.post(orderRef.current.appSiteUrl + '/api/donor/digitalReceipt/' + orderRef.current.orderId).then(retUrl => {
                    console.log(retUrl);
                    window.location.href = retUrl.data;
                }).catch(e => {
                    window.location.href = "/";

                })
            }
            else {
                alert(res.data);
                submittingRef.current = false;
                setIsSubmitting(false);
            }
        }).catch(e => {
            var message = "Error Processing Order: /r/n" + e
            logMessage(message, 4)
            alert("Order was unsuccessful");
        });

        //setIsSubmitting(false);
        //this.setState({ isSubmitting: false, alertMessage: 'The form was submitted. Check the console to see the output data.' });
    };

    const logMessage = (message, level) => {
        var data = {
            message: message,
            level: level,
        };

        axios.post('/api/Logger/Log/', data)
            .then(response => {
                //    console.log("LOGGER: " + response.data);
            })
            .catch(error => {
                //    console.error("LOGGER: " + error);
            });
    };

    useEffect(x => {

        OrdersService.getFundraiserOrder(orderId).then((x) => {
            setOrder(x);
        });
        enforceValidation();


        // setTimeout(() => {
        //    
        // }, 100);


        return () => { };


    }, []);
    return (
        <>
            <h2>Checkout{(order && order.testMode ? " *TEST_MODE*" : "")}</h2>

            <div className="row g-5">
                <div className="col-md-7 col-lg-8">
                    <form className="needs-validation" noValidate onSubmit={handleSubmit}>
                        <h4 className="mb-3">Billing Address</h4>
                        <BillingAddressForm 
                            shippingRequired={shippingInfo && shippingInfo.shippingType === 2}
                            sameAddress={sameAddress}
                            onAddressChange={(field, value) => {
                            if (order) {
                                const updatedOrder = { ...order };
                                // Handle name fields (always update regardless of sameAddress)
                                if (field === 'firstName') updatedOrder.supporterFirstName = value;
                                else if (field === 'lastName') updatedOrder.supporterLastName = value;
                                // Handle address fields (only update shipping address if sameAddress is true)
                                else if (sameAddress) {
                                    if (field === 'address') updatedOrder.shippingAddress = value;
                                    else if (field === 'address2') updatedOrder.shippingAddress2 = value;
                                    else if (field === 'city') updatedOrder.shippingCity = value;
                                    else if (field === 'state') updatedOrder.shippingState = value;
                                    else if (field === 'zip') updatedOrder.shippingZip = value;
                                }
                                setOrder(updatedOrder);
                            }
                        }} />
                        <hr className="my-4" />
                        <div className="mb-3">
                            <h4 className="mb-3">Delivery Address</h4>
                            <FormGroup check className="mb-3">
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        defaultChecked={sameAddress}
                                        onChange={
                                            (e) => {
                                                const isChecked = e.target.checked;
                                                setSameAddress(isChecked);
                                                
                                                if (order) {
                                                    const updatedOrder = { ...order };
                                                    
                                                    if (isChecked) {
                                                        // When checking, copy billing address to shipping address
                                                        // Get values from the billing form inputs
                                                        const billingFirstName = document.getElementById('firstName')?.value || '';
                                                        const billingLastName = document.getElementById('lastName')?.value || '';
                                                        const billingAddress = document.getElementById('address')?.value || '';
                                                        const billingAddress2 = document.getElementById('address2')?.value || '';
                                                        const billingCity = document.getElementById('city')?.value || '';
                                                        const billingState = document.getElementById('state')?.value || '';
                                                        const billingZip = document.getElementById('zip')?.value || '';
                                                        
                                                        updatedOrder.supporterFirstName = billingFirstName;
                                                        updatedOrder.supporterLastName = billingLastName;
                                                        updatedOrder.shippingAddress = billingAddress;
                                                        updatedOrder.shippingAddress2 = billingAddress2;
                                                        updatedOrder.shippingCity = billingCity;
                                                        updatedOrder.shippingState = billingState;
                                                        updatedOrder.shippingZip = billingZip;
                                                    } else {
                                                        // When unchecking, copy delivery address to shipping address
                                                        updatedOrder.supporterFirstName = deliveryAddress.firstName || '';
                                                        updatedOrder.supporterLastName = deliveryAddress.lastName || '';
                                                        updatedOrder.shippingAddress = deliveryAddress.address || '';
                                                        updatedOrder.shippingAddress2 = deliveryAddress.address2 || '';
                                                        updatedOrder.shippingCity = deliveryAddress.city || '';
                                                        updatedOrder.shippingState = deliveryAddress.state || '';
                                                        updatedOrder.shippingZip = deliveryAddress.zip || '';
                                                    }
                                                    
                                                    setOrder(updatedOrder);
                                                }
                                            }
                                        }
                                    />{""}
                                    <strong>Same as Billing Address</strong>
                                </Label>
                            </FormGroup>
                            <DeliveryAddressForm 
                                show={!sameAddress}
                                shippingRequired={shippingInfo && shippingInfo.shippingType === 2}
                                deliveryAddress={deliveryAddress}
                                onAddressChange={(field, value) => {
                                // Update the delivery address state
                                setDeliveryAddress(prev => ({
                                    ...prev,
                                    [field]: value
                                }));
                                
                                if (order) {
                                    const updatedOrder = { ...order };
                                    // Handle name fields (always update regardless of sameAddress)
                                    if (field === 'firstName') updatedOrder.supporterFirstName = value;
                                    else if (field === 'lastName') updatedOrder.supporterLastName = value;
                                    // Handle address fields (only update shipping address if not sameAddress)
                                    else if (!sameAddress) {
                                        if (field === 'address') updatedOrder.shippingAddress = value;
                                        else if (field === 'address2') updatedOrder.shippingAddress2 = value;
                                        else if (field === 'city') updatedOrder.shippingCity = value;
                                        else if (field === 'state') updatedOrder.shippingState = value;
                                        else if (field === 'zip') updatedOrder.shippingZip = value;
                                    }
                                    setOrder(updatedOrder);
                                }
                            }} />
                        </div>
                        <hr className="my-4" />
                        <h4>Payment</h4>
                        <div className="row gy-3">
                            <div className="col-md-6">
                                <label htmlFor="holderName" className="form-label">Name on card</label>
                                <input type="text" className="form-control" id="holderName" placeholder="" required />
                                <small className="text-muted">Full name as displayed on card</small>
                                <div className="invalid-feedback">
                                    Name on card is required
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label htmlFor="cardNumber" required className="form-label">Credit card number</label>
                                <input type="text" className="form-control" id="cardNumber" placeholder="" required />
                                <div className="invalid-feedback">
                                    Card number is required
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label htmlFor="expiryMonth" className="form-label">Exp. Month</label>
                                <input type="text" className="form-control" id="expiryMonth" placeholder="" required />
                                <small className="text-muted">2-digit month</small>
                                <div className="invalid-feedback">
                                    Card expiration month is required
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label htmlFor="expiryYear" className="form-label">Exp. Year</label>
                                <input type="text" className="form-control" id="expiryYear" placeholder="" required />
                                <small className="text-muted">4-digit year</small>
                                <div className="invalid-feedback">
                                    Card expiration year is required
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label htmlFor="cvv" className="form-label">CVV</label>
                                <input type="text" className="form-control" id="cvv" placeholder="" required />
                                <div className="invalid-feedback">
                                    Card CVV is required
                                </div>
                            </div>

                            <div className="col-md-12">
                                <div className="text-danger" >
                                    <ul id="ccErrors"></ul>
                                </div>
                            </div>
                        </div>
                        <hr className="my-4" />
                        <h4>Order Summary</h4>
                        <OrderedTable 
                            order={order} 
                            onShippingUpdate={setShippingInfo}
                        />
                        <hr className="my-4" />
                        <button
                            className="w-100 btn btn-primary btn-lg" type="submit" disabled={isSubmitting}>Submit
                        </button>
                    </form>
                </div>
            </div>
        </>
    );

}
